import React, { useContext, useEffect, useRef } from 'react';
import FormInput from '../../common/FormInput';
import DateInput from '../../common/dateInput/DateInput';
import TextInput from '../../common/textInput/TextInput';
import TextArea from '../../common/textArea/TextArea';
import {
    CosmeticRepairRequestType,
    CosmeticErrors,
    DamageTypes,
    DamageDepth,
    DamageLength,
    DamageLengthOptions,
    Panels,
    DamageTypesExample
} from './CosmeticRepairRequestTypes';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import SelectableImageMultiple from '../../common/SelectableImageMultiple';
import { ImageOption, SelectOpt } from '../../common/CommonTypes';
import DentNoPaintDamage from '../../../images/Cosmetic_DentNoPaintDamage.jpg';
import DentPaintDamage from '../../../images/Cosmetic_DentPaintDamage.jpg';
import Scratch from '../../../images/Cosmetic_Scratch.jpg';
import StoneChip from '../../../images/Cosmetic_StoneChip.jpg';
import Hail from '../../../images/Cosmetic_Hail.jpg';
import CosmeticCloseup from '../../../images/Cosmetic_Scratch.jpg';
import CosmeticDistance from '../../../images/Cosmetic_Distance.jpg';
import SelectInput from '../../common/selectInput/SelectInput';
import ImageUploader from '../../common/fileUpload/ImageUploader';
import {
    UploadImageTypes,
    PageNames,
    Locations,
    FeatureState
} from '../../../Constants/Common';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import SVGPanelSelector from './SVGPanelSelector';
import MobileSVGPanelSelector from './MobileSVGPanelSelector';
import MediaQuery from 'react-responsive';
import { TranslationHelper } from 'classes/TranslationHelper';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { SiteContext } from '../../../context/SiteContext';

export type CosmeticRepairFormProps = {
    repairRequest: CosmeticRepairRequestType;
    formErrors?: CosmeticErrors;
    errorSummaryTitle: string;
    onChange: (key: string, value: string) => void;
    onChangeMileage: (event: React.FormEvent<HTMLInputElement>) => void;
    onChangeDamageType: (selectionName: string) => void;
    onChangeDamageDepth: (selecetdValue: string) => void;
    showDamageDepthQuestion: () => boolean;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onSelectedPanelChanged: (selected: boolean, panel: Panels) => void;
};

const CosmeticRepairForm = ({
    repairRequest,
    formErrors,
    onChange,
    errorSummaryTitle,
    onChangeMileage,
    onChangeDamageType,
    onChangeDamageDepth,
    showDamageDepthQuestion,
    onUploadImageChange,
    onSelectedPanelChanged
}: CosmeticRepairFormProps): JSX.Element => {
    const errorlist = (): Array<string | undefined> => {
        const values = [
            formErrors?.dateOfIncident,
            formErrors?.mileage,
            formErrors?.selectedPanels,
            formErrors?.incidentOccured,
            formErrors?.damageType,
            formErrors?.damageDepth,
            formErrors?.damageLength,
            formErrors?.closeUpImage,
            formErrors?.distanceImage
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };
    const modalBottom = useRef(null);
    const { t, i18n } = useTranslation();
    const { features } = useContext(SiteContext);

    useEffect(() => {
        if (
            formErrors !== undefined &&
            Object.keys(formErrors).length !== 0 &&
            modalBottom.current
        ) {
            modalBottom.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.CosmeticRepairRequests);
    }, [formErrors]);

    const onChangeEvent = (selectedValue: string, key: string) => {
        onChange(key, selectedValue);
    };

    const damageTypeOptions: ImageOption[] = [
        {
            name: DamageTypes.DentNoPaintDamage,
            image: DentNoPaintDamage,
            altText: DamageTypes.DentNoPaintDamage,
            label: t('dent_with_no_paint_damage')
        },
        {
            name: DamageTypes.DentPaintDamage,
            image: DentPaintDamage,
            altText: DamageTypes.DentPaintDamage,
            label: t('dent_with_paint_damage')
        },
        {
            name: DamageTypes.Scratch,
            image: Scratch,
            altText: DamageTypes.Scratch,
            label: t('scratch')
        },
        {
            name: DamageTypes.StoneChip,
            image: StoneChip,
            altText: DamageTypes.StoneChip,
            label: t('stone_chip')
        }
    ];

    if (
        i18n.language.toLowerCase() === Locations.Us &&
        features.CosmeticDamage === FeatureState.On
    )
        damageTypeOptions.push({
            name: DamageTypes.Hail,
            image: Hail,
            altText: DamageTypes.Hail,
            label: t('hail')
        });

    const damageTypeExamples: ImageOption[] = [
        {
            name: DamageTypesExample.CloseUpImage,
            image: CosmeticCloseup,
            altText: DamageTypesExample.CloseUpImage,
            label: DamageTypesExample.CloseUpImage
        },
        {
            name: DamageTypesExample.DistanceImage,
            image: CosmeticDistance,
            altText: DamageTypesExample.DistanceImage,
            label: DamageTypesExample.DistanceImage
        }
    ];

    const damageDepthOptions: SelectOpt[] = [
        {
            id: DamageDepth.Yes,
            text: DamageDepth.Yes,
            value: DamageDepth.Yes,
            disabled: false
        },
        {
            id: DamageDepth.No,
            text: DamageDepth.No,
            value: DamageDepth.No,
            disabled: false
        }
    ];
    const damageLengthOptions: DamageLengthOptions[] = [
        {
            id: t('cosmetic_damage_length.under_ten'),
            text: t('cosmetic_damage_length.under_ten'),
            value: DamageLength.UnderTen,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.ten_to_fifteen'),
            text: t('cosmetic_damage_length.ten_to_fifteen'),
            value: DamageLength.TenToFifteen,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.fifteen_to_twenty'),
            text: t('cosmetic_damage_length.fifteen_to_twenty'),
            value: DamageLength.FifteenToTwenty,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.twenty_to_twentyfive'),
            text: t('cosmetic_damage_length.twenty_to_twentyfive'),
            value: DamageLength.TwentyToTwentyFive,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.twentyfive_to_thirty'),
            text: t('cosmetic_damage_length.twentyfive_to_thirty'),
            value: DamageLength.TwentyFiveToThirty,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.thirty_to_thirtyfive'),
            text: t('cosmetic_damage_length.thirty_to_thirtyfive'),
            value: DamageLength.ThirtyToThirtyFive,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.thirtyfive_to_fourty'),
            text: t('cosmetic_damage_length.thirtyfive_to_fourty'),
            value: DamageLength.ThirtyFiveToFourty,
            disabled: false
        },
        {
            id: t('cosmetic_damage_length.over_fourty'),
            text: t('cosmetic_damage_length.over_fourty'),
            value: DamageLength.OverFourty,
            disabled: false
        }
    ];

    const getPanelListItem = (panel: Panels) => {
        return (
            <li key={panel} className='list-group-item panel-list'>
                {TranslationHelper.getTranslatedText(panel.toString())}
            </li>
        );
    };

    const getClickedPanels = () => {
        return (
            <ul className='list-group list-group-flush'>
                {repairRequest.selectedPanels.map((panel) =>
                    getPanelListItem(panel)
                )}
            </ul>
        );
    };

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    const uploadImageLabel =
        i18next.language.toLocaleLowerCase() === 'us'
            ? t('upload_image_distance_3ft_label')
            : t('upload_image_distance_label');
    const distanceImageLabel =
        i18next.language.toLocaleLowerCase() === 'us'
            ? t('upload_image_distance_3ft_field')
            : t('upload_image_distance_field');

    return (
        <div className='card page-content no-border'>
            <div className='card-body page-content-body'>
                <FormInput
                    id='incident-date-forminput'
                    label={t('alloy_repair_request.date_of_incident')}
                    helpText={t('help_text.cosmetic_date_of_incident')}
                    errorText={formErrors?.dateOfIncident}>
                    <DateInput
                        name='Date of incident'
                        useJson
                        forceTimeReset
                        value={repairRequest?.dateOfIncident}
                        onDateChanged={(dateString) =>
                            onChangeEvent(dateString, 'dateOfIncident')
                        }
                        maximumDate={maxDate()}
                    />
                </FormInput>
                <FormInput
                    id='mileage-forminput'
                    label={t('approximate_mileage')}
                    helpText={t('help_text.cosmetic_approx_mileage')}
                    errorText={formErrors?.mileage}>
                    <div className='row repair-request-field-container'>
                        <div className='col-xs-12 col-lg-6'>
                            <TextInput
                                name='approx-mileage'
                                value={repairRequest?.mileage}
                                onChange={onChangeMileage}
                            />
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='selected-panels'
                    label={t('cosmetic_repair_panel_label')}
                    helpText={t('help_text.cosmetic_panel_selection')}
                    errorText={formErrors?.selectedPanels}>
                    <MediaQuery maxWidth={991}>
                        <MobileSVGPanelSelector
                            selectedPanels={repairRequest.selectedPanels}
                            onSelectionChange={onSelectedPanelChanged}
                        />
                    </MediaQuery>
                    <MediaQuery minWidth={992}>
                        <SVGPanelSelector
                            selectedPanels={repairRequest.selectedPanels}
                            onSelectionChange={onSelectedPanelChanged}
                        />
                    </MediaQuery>
                    {getClickedPanels()}
                    {repairRequest.selectedPanels &&
                        repairRequest.selectedPanels.length > 1 && (
                            <div
                                className='alert alert-warning panel-selection-alert-container'
                                role='alert'>
                                {t('selected_multiple_panels')}
                            </div>
                        )}
                </FormInput>
                <FormInput
                    id='other-details-forminput'
                    label={t('cosmetic_repair_brief_description')}
                    errorText={formErrors?.incidentOccured}>
                    <TextArea
                        name='incident-occured'
                        rows={3}
                        value={repairRequest?.incidentOccured}
                        innerClassName='fullscreen'
                        onChange={(e: React.FormEvent<HTMLTextAreaElement>) =>
                            onChangeEvent(
                                e.currentTarget.value,
                                'incidentOccured'
                            )
                        }
                    />
                </FormInput>
                <FormInput
                    id='damage-type-forminput'
                    label={t('cosmetic_repair_damage_type_label')}
                    helpText={t('help_text.cosmetic_type_of_damage')}
                    errorText={formErrors?.damageType}>
                    <SelectableImageMultiple
                        imageOptions={damageTypeOptions}
                        selectedItem={repairRequest?.damageType}
                        onChange={onChangeDamageType}
                    />
                    {repairRequest?.damageType === DamageTypes.Hail && (
                        <div
                            className='alert alert-warning panel-selection-alert-container'
                            role='alert'>
                            {t('hail_limitation_message')}
                        </div>
                    )}
                </FormInput>
                {showDamageDepthQuestion() && (
                    <FormInput
                        id='damage-depth-forminput'
                        label={t(
                            'damage_deeper_than_the_depth_of_a_pound_coin_label'
                        )}
                        helpText={t('help_text.cosmetic_damage_depth')}
                        errorText={formErrors?.damageDepth}>
                        <SelectInput
                            name='damage-depth'
                            loadingText={t('loading')}
                            value={repairRequest?.damageDepth}
                            options={damageDepthOptions}
                            onChange={onChangeDamageDepth}
                            forceDefault
                            defaultIfOneOption
                            addDefaultOption
                            defaultText={t('select_default')}
                        />
                    </FormInput>
                )}
                <FormInput
                    id='damage-length-forminput'
                    label={t('length_of_damage')}
                    helpText={t('help_text.cosmetic_damage_length')}
                    errorText={formErrors?.damageLength}>
                    <SelectInput
                        name='damage-length'
                        loadingText={t('loading')}
                        value={repairRequest?.damageLength}
                        options={damageLengthOptions}
                        onChange={(selectedValue: string) =>
                            onChangeEvent(selectedValue, 'damageLength')
                        }
                        forceDefault
                        defaultIfOneOption
                        addDefaultOption
                        defaultText={t('select_default')}
                    />
                </FormInput>
                <FormInput
                    id='closeup-image-forminput'
                    label={t('image_of_measured_damage_label')}
                    helpText={t('help_text.cosmetic_closeup_image')}
                    errorText={formErrors?.closeUpImage}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <p
                                className='example-image-text'
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>
                                {t('example')}
                            </p>
                            <img
                                id='damage-example-images'
                                className='example-images'
                                src={damageTypeExamples[0].image}
                                alt={damageTypeExamples[0].altText}
                            />
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t('image_of_measured_damage_field')}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='close-up-damage-preview'
                                        title={t('upload_image')}
                                        UploadedImage={repairRequest?.uploadedImages.find(
                                            (ui) =>
                                                ui.type ==
                                                UploadImageTypes.CloseUp
                                        )}
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='distance-image-forminput'
                    label={uploadImageLabel}
                    helpText={t('help_text.cosmetic_distance_image')}
                    errorText={formErrors?.distanceImage}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <p
                                className='example-image-text'
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>
                                {t('example')}
                            </p>
                            <img
                                className='example-images'
                                id='distance-damage-example'
                                src={damageTypeExamples[1].image}
                                alt={damageTypeExamples[1].altText}
                            />
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {distanceImageLabel}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='distance-damage-preview'
                                        title={t('upload_image')}
                                        UploadedImage={repairRequest?.uploadedImages.find(
                                            (ui) =>
                                                ui.type ==
                                                UploadImageTypes.Distance
                                        )}
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='additional-image-forminput'
                    label={t('additional_images_label')}
                    helpText={t('help_text.cosmetic_additional_image')}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t('additional_image_1')}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='additional-image-preview'
                                        title={t('upload_image')}
                                        UploadedImage={repairRequest?.uploadedImages.find(
                                            (ui) =>
                                                ui.type ==
                                                UploadImageTypes.AdditionalImage1
                                        )}
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t('additional_image_2')}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='additional-image-preview'
                                        title={t('upload_image')}
                                        UploadedImage={repairRequest?.uploadedImages.find(
                                            (ui) =>
                                                ui.type ==
                                                UploadImageTypes.AdditionalImage2
                                        )}
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
            </div>
            <div className='card-footer page-content-footer'>
                {formErrors && (
                    <ErrorSummary
                        title={errorSummaryTitle}
                        errors={errorlist()}
                        className='card-error'
                    />
                )}
            </div>
        </div>
    );
};

export default CosmeticRepairForm;
